import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  StockItemDetail,
  StockLocation,
  StockTransferResult,
  TransferStockBody,
} from "@shared-core/data/api-types";
import { ApiError } from "@shared-core/data/api/errors";
import { formatCount } from "@shared-core/domain/formatters";
import {
  holdingsWithStockAt,
  locationsWithStock,
} from "@shared-core/domain/stock";
import { Button } from "../../app/components/primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../app/components/primitives/dialog";
import { Textarea } from "../../app/components/primitives/textarea";
import { BalanceLine, batchLabel } from "./AdjustStockDialog";
import {
  FieldShell,
  type FieldErrorMap,
  TextField,
  selectClassName,
  toFieldErrorMap,
} from "./StockFormField";

interface TransferStockDialogProps {
  item: StockItemDetail;
  /** Active warehouses, any of which can receive stock. */
  locations: StockLocation[];
  onClose: () => void;
  onTransferred: (result: StockTransferResult) => void;
}

export function TransferStockDialog({
  item,
  locations,
  onClose,
  onTransferred,
}: TransferStockDialogProps) {
  const dataAdapter = useDataAdapter();
  const sources = locationsWithStock(
    item.holdings.filter((h) => h.location_is_active),
  );
  const [fromId, setFromId] = useState(
    sources.length === 1 ? sources[0]!.location_id : "",
  );
  const [batchId, setBatchId] = useState(() => oldestBatchAt(item, fromId));
  const [toId, setToId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [note, setNote] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrorMap>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const held = (locationId: string) =>
    item.holdings
      .filter(
        (h) =>
          h.location_id === locationId &&
          (!item.tracks_batches || h.batch_id === batchId),
      )
      .reduce((sum, h) => sum + h.on_hand, 0);
  const parsed = Number(quantity);
  const hasQuantity =
    quantity.trim() !== "" && Number.isInteger(parsed) && parsed > 0;
  const batchesHere = holdingsWithStockAt(item.holdings, fromId);

  function chooseSource(next: string) {
    setFromId(next);
    setBatchId(oldestBatchAt(item, next));
    if (toId === next) setToId("");
  }

  async function handleSave() {
    const missing: FieldErrorMap = {};
    if (!fromId) missing.from_location_id = "Choose where the stock is now";
    if (!toId) missing.to_location_id = "Choose where it's going";
    if (item.tracks_batches && !batchId)
      missing.batch_id = "Choose which batch";
    if (Object.keys(missing).length) {
      setFieldErrors(missing);
      return;
    }
    setIsSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      const body: TransferStockBody = {
        from_location_id: fromId,
        to_location_id: toId,
        quantity: parsed,
        note: note.trim() || null,
      };
      if (item.tracks_batches) body.batch_id = batchId;
      onTransferred(await dataAdapter.transferStock(item.id, body));
    } catch (error) {
      console.error("stock.item.transfer_failed", {
        status: error instanceof ApiError ? error.status : undefined,
      });
      const map = toFieldErrorMap(error);
      setFieldErrors(map);
      setFormError(
        Object.keys(map).length > 0
          ? "Please fix the highlighted fields and try again."
          : error instanceof ApiError && error.status === 409
            ? "The stock item is archived, or one of the warehouses is no longer active."
            : "Couldn't move this stock. Please try again.",
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Transfer stock</DialogTitle>
          <DialogDescription>
            {item.stock_code} · Moves stock between warehouses. The total on
            hand doesn't change.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
          {formError && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300"
            >
              <AlertTriangle className="size-4 shrink-0 mt-0.5" />
              {formError}
            </div>
          )}

          <FieldShell
            id="transfer-from"
            label="From"
            error={fieldErrors.from_location_id}
          >
            {(aria) => (
              <select
                id="transfer-from"
                className={selectClassName}
                value={fromId}
                onChange={(e) => chooseSource(e.target.value)}
                {...aria}
              >
                <option value="">Choose a warehouse…</option>
                {sources.map((l) => (
                  <option key={l.location_id} value={l.location_id}>
                    {l.location_name ?? l.location_id}
                  </option>
                ))}
              </select>
            )}
          </FieldShell>

          {item.tracks_batches && (
            <FieldShell
              id="transfer-batch"
              label="Batch"
              error={fieldErrors.batch_id}
              hint="The oldest batch with stock there is chosen first."
            >
              {(aria) => (
                <select
                  id="transfer-batch"
                  className={selectClassName}
                  value={batchId}
                  onChange={(e) => setBatchId(e.target.value)}
                  disabled={!fromId}
                  {...aria}
                >
                  <option value="">Choose a batch…</option>
                  {batchesHere.map((h) => (
                    <option key={h.id} value={h.batch_id ?? ""}>
                      {batchLabel(h.batch_no, h.artwork_revision_id)} (
                      {formatCount(h.on_hand)})
                    </option>
                  ))}
                </select>
              )}
            </FieldShell>
          )}

          <FieldShell
            id="transfer-to"
            label="To"
            error={fieldErrors.to_location_id}
          >
            {(aria) => (
              <select
                id="transfer-to"
                className={selectClassName}
                value={toId}
                onChange={(e) => setToId(e.target.value)}
                {...aria}
              >
                <option value="">Choose a warehouse…</option>
                {locations
                  .filter((l) => l.id !== fromId)
                  .map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
              </select>
            )}
          </FieldShell>

          <TextField
            id="transfer-quantity"
            label="Quantity to move"
            value={quantity}
            onChange={setQuantity}
            error={fieldErrors.quantity}
            inputMode="numeric"
          />

          <FieldShell
            id="transfer-note"
            label="Note (optional)"
            error={fieldErrors.note}
            hint="e.g. the carrier or the reason for the move"
          >
            {(aria) => (
              <Textarea
                id="transfer-note"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                {...aria}
              />
            )}
          </FieldShell>

          {(fromId || toId) && (
            <div
              className="rounded-lg border border-border bg-surface px-4 py-3 text-sm space-y-1"
              aria-live="polite"
            >
              {fromId && (
                <BalanceLine
                  label="From"
                  before={held(fromId)}
                  delta={hasQuantity ? -parsed : null}
                />
              )}
              {toId && (
                <BalanceLine
                  label="To"
                  before={held(toId)}
                  delta={hasQuantity ? parsed : null}
                />
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={isSaving || !hasQuantity}
          >
            {isSaving ? "Moving…" : "Transfer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function oldestBatchAt(item: StockItemDetail, locationId: string): string {
  if (!item.tracks_batches) return "";
  return holdingsWithStockAt(item.holdings, locationId)[0]?.batch_id ?? "";
}
